// frontend/src/components/celebration-chat/TemplateCards.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, SkipForward } from 'lucide-react';

interface TemplateSuggestion {
  key: string;
  name: string;
  description: string;
  matchScore: number;
}

interface TemplateCardsProps {
  templates: TemplateSuggestion[];
  onSelect: (templateKey: string, templateName: string) => void;
  onSkip: () => void;
}

export const TemplateCards: React.FC<TemplateCardsProps> = ({ templates, onSelect, onSkip }) => {
  const { t } = useTranslation('celebrations');
  return (
    <div className="space-y-2 max-w-md">
      <p className="text-sm text-muted-foreground mb-2">{t('chat.templateCards.chooseTemplate', { defaultValue: 'Choose a template to get started' })}</p>
      {templates.map((template, index) => (
        <Card
          key={template.key}
          className="cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => onSelect(template.key, template.name)}
        >
          <CardContent className="p-3 flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{template.name}</span>
                {index === 0 && (
                  <Badge variant="default" className="text-xs">{t('chat.templateCards.bestMatch', { defaultValue: 'Best match' })}</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{template.description}</p>
            </div>
          </CardContent>
        </Card>
      ))}
      <Button
        variant="ghost"
        size="sm"
        className="w-full text-muted-foreground"
        onClick={onSkip}
      >
        <SkipForward className="h-4 w-4 mr-2" />
        {t('chat.templateCards.skip', { defaultValue: 'Skip template' })}
      </Button>
    </div>
  );
};
